import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/components/button/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { OPEN_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getLocale } from '@/configuration/lang/langSlice';
import { useAppSelector } from '@/configuration/setup/hooks';
import { getAccessToken } from '@/configuration/tokenHandling/tokenSlice';
import {
    MFE_DIALOG_PREFIX,
    TABLE_BRAKE_LINING,
    TABLE_FILL_LEVEL,
    TABLE_MAINTENANCE_COMPONENTS,
    TABLE_SETTINGS_DIALOG_PATH,
    TABLE_TYRE_PRESSURE,
} from '@/features/common/components/dialogs/dialogConstants';
import { buildUrl } from '@/features/common/components/dialogs/RemoteDialog';
import { TableSettingsTooltip } from '@/features/common/TableSettingsTooltip';

type RightToolBarProps = {
    settingsDialog:
        | typeof TABLE_BRAKE_LINING
        | typeof TABLE_FILL_LEVEL
        | typeof TABLE_MAINTENANCE_COMPONENTS
        | typeof TABLE_TYRE_PRESSURE;
    onExportClick: () => void;
    isDownloading: boolean;
    leadingContent?: React.ReactNode;
    centerContent?: React.ReactNode;
    trailingContent?: React.ReactNode;
};
const CommonRightToolBar = ({
    leadingContent,
    centerContent,
    trailingContent,
    onExportClick,
    settingsDialog,
    isDownloading,
}: RightToolBarProps) => {
    const { sendMessageToParent } = usePostMessageSender();
    const token = useAppSelector(getAccessToken);
    const locale = useAppSelector(getLocale) || DEFAULT_LOCALE;

    const handleSettingsClick = useCallback(() => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${OPEN_DIALOG_EVENT}`,
            payload: {
                src: buildUrl(`${TABLE_SETTINGS_DIALOG_PATH}/${settingsDialog}`, locale, token),
                dialogProps: {
                    bsSize: Dialog.SIZE_SM,
                },
            },
        });
    }, [sendMessageToParent, settingsDialog, locale, token]);
    return (
        <div className="common-right-toolbar space-x-10">
            {leadingContent}
            <OverlayTrigger placement={Tooltip.BOTTOM_START} overlay={<TableSettingsTooltip />}>
                <Button iconOnly className="btn btn-default settings" onClick={handleSettingsClick}>
                    <span className="rioglyph rioglyph-settings" role="img" aria-label="Settings icon" />
                </Button>
            </OverlayTrigger>
            {centerContent}
            <Button
                className="btn btn-default align-top"
                type="button"
                onClick={onExportClick}
                disabled={isDownloading}
            >
                <FormattedMessage id={'intl-msg:vehicle-diagnostics.button.csvExport'} />
            </Button>
            {trailingContent}
        </div>
    );
};

export default CommonRightToolBar;
